import { css, StyleSheet } from 'aphrodite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { policiesApi, Policy, orgsApi } from '@/features/org/api/orgs.api';
import { orgStyles as s } from '@/features/org/components/org.styles';
import ScopePicker from '@/features/org/components/permissions/scope-picker/ScopePicker';
import SettingsLayout from '@/features/org/components/settings/settings-layout/SettingsLayout';
import {
  ACTION_GROUPS,
  ALL_ACTION_IDS,
  ActionDef,
} from '@/features/org/utils/action-catalogue/actionCatalogue';
import {
  ScopeSelection,
  expandToArns,
  parseArns,
  describeScope,
} from '@/features/org/utils/scope-arn/scopeArn';
import { useParams } from '@/shared/hooks/use-router/react-router-hooks';

/**
 * Permissions = Policies CRUD.
 *
 * Left column: list of policies (built-in + this org's custom).
 * Right column: detail editor for the selected policy.
 *
 *   - Built-ins are read-only (no scope picker, just a flat view).
 *   - Custom policies: per-action scope picker. Each action can be set
 *     to "All current + future" or pinned to specific apps / envs.
 */
export default function PermissionsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [policies, setPolicies] = useState<Policy[] | null>(null);
  const [resources, setResources] = useState<{
    apps: { id: string; name: string }[];
    envsByAppId: Record<string, { id: string; name: string; kind: string }[]>;
  }>({ apps: [], envsByAppId: {} });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    orgsApi
      .bySlug(slug)
      .then((r) => setOrgId(r.org.id))
      .catch((e) => setErr(e.message));
    orgsApi
      .resources(slug)
      .then(setResources)
      .catch(() => {
        /* non-fatal */
      });
  }, [slug]);

  const reload = useCallback(async () => {
    try {
      const r = await policiesApi.list(orgId ?? undefined);
      setPolicies(r.policies);
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) reload();
  }, [orgId, reload]);

  const selected = useMemo(
    () => policies?.find((p) => p.id === selectedId) ?? null,
    [policies, selectedId],
  );

  const handleNew = () => setSelectedId('__new__');
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this permission? Any role still attached must be detached first.')) return;
    try {
      await policiesApi.remove(id);
      await reload();
      setSelectedId(null);
    } catch (e: any) {
      setErr(e.message);
    }
  };

  return (
    <SettingsLayout>
      <div className={css(s.page)} style={{ maxWidth: 'none' }}>
        <div className={css(s.pageHeader)}>
          <div>
            <div className={css(s.pageTitle)}>Permissions</div>
            <div className={css(s.pageSubtitle)}>
              A permission is a named set of actions. Each action can target all current + future
              resources, or pin to specific apps + envs.
            </div>
          </div>
          <button className={css(s.btn)} onClick={handleNew}>
            + New permission
          </button>
        </div>

        {err && <div className={css(s.errorBox)}>{err}</div>}

        <div className={css(pp.split)}>
          <div className={css(pp.list)}>
            {!policies ? (
              <div className={css(s.empty)}>Loading…</div>
            ) : (
              policies.map((p) => (
                <button
                  key={p.id}
                  className={css(pp.listItem, selectedId === p.id && pp.listItemActive)}
                  onClick={() => setSelectedId(p.id)}
                >
                  <div className={css(pp.listName)}>{p.name}</div>
                  <div className={css(pp.listDesc)}>
                    {p.description || `${p.actions.length} ARN(s)`}
                  </div>
                  {p.isBuiltin && <div className={css(pp.builtinChip)}>BUILT-IN</div>}
                </button>
              ))
            )}
          </div>
          <div className={css(pp.detail)}>
            {selectedId === '__new__' && orgId && (
              <PolicyEditor
                orgId={orgId}
                resources={resources}
                onSaved={(p) => {
                  reload();
                  setSelectedId(p.id);
                }}
                onCancel={() => setSelectedId(null)}
              />
            )}
            {selected && selectedId !== '__new__' && (
              <PolicyEditor
                key={selected.id}
                orgId={orgId!}
                initial={selected}
                resources={resources}
                onSaved={() => reload()}
                onDelete={selected.isBuiltin ? undefined : () => handleDelete(selected.id)}
                onCancel={() => setSelectedId(null)}
              />
            )}
            {!selectedId && (
              <div className={css(s.empty)}>Pick a permission on the left to view or edit.</div>
            )}
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
}

function PolicyEditor({
  orgId,
  initial,
  resources,
  onSaved,
  onDelete,
  onCancel,
}: {
  orgId: string;
  initial?: Policy;
  resources: {
    apps: { id: string; name: string }[];
    envsByAppId: Record<string, { id: string; name: string; kind: string }[]>;
  };
  onSaved: (p: Policy) => void;
  onDelete?: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  // Per-action scope state. Map<actionId, ScopeSelection>. An action
  // NOT in the map means "not granted by this policy".
  const [scopes, setScopes] = useState<Map<string, ScopeSelection>>(() =>
    initial ? parseArns(initial.actions) : new Map(),
  );
  // Which checked actions have their picker panel open. Closed by
  // default — the chip summary is enough most of the time.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const readOnly = !!initial?.isBuiltin;
  const appNameById = useMemo(
    () => new Map(resources.apps.map((a) => [a.id, a.name])),
    [resources.apps],
  );

  const toggleAction = (action: ActionDef) => {
    if (readOnly) return;
    setScopes((prev) => {
      const next = new Map(prev);
      if (next.has(action.id)) {
        next.delete(action.id);
      } else {
        // Default to "All current + future" per the product spec.
        next.set(action.id, { kind: 'all' });
      }
      return next;
    });
  };

  const setActionScope = (actionId: string, sel: ScopeSelection) => {
    if (readOnly) return;
    setScopes((prev) => {
      const next = new Map(prev);
      next.set(actionId, sel);
      return next;
    });
  };

  const togglePicker = (actionId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) next.delete(actionId);
      else next.add(actionId);
      return next;
    });
  };

  const handleSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      // Flatten per-action selections into the ARN list the API expects.
      const allArns: string[] = [];
      for (const [actionId, sel] of scopes) {
        const action = ACTION_GROUPS.flatMap((g) => g.actions).find((a) => a.id === actionId);
        if (!action) continue;
        allArns.push(...expandToArns(action, sel, orgId));
      }
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        actions: allArns,
      };
      const res = initial
        ? await policiesApi.update(initial.id, payload)
        : await policiesApi.create({ ...payload, orgId });
      onSaved(res.policy);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {readOnly && (
        <div className={css(pp.readOnlyBanner)}>
          Built-in permission — read-only. Create a new permission to customize.
        </div>
      )}
      {err && <div className={css(s.errorBox)}>{err}</div>}
      <div className={css(s.field)}>
        <label className={css(s.label)}>Name</label>
        <input
          className={css(s.input)}
          value={name}
          disabled={readOnly}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. PublishProdProductionOnly"
        />
      </div>
      <div className={css(s.field)}>
        <label className={css(s.label)}>Description</label>
        <input
          className={css(s.input)}
          value={description}
          disabled={readOnly}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this permission lets a role do"
        />
      </div>
      <div className={css(pp.actionsSummary)}>
        {scopes.size}/{ALL_ACTION_IDS.length} actions granted
      </div>
      <div className={css(pp.actionGroups)}>
        {ACTION_GROUPS.map((g) => (
          <div key={g.label} className={css(pp.actionGroup)}>
            <div className={css(pp.actionGroupHdr)}>
              <span className={css(pp.actionGroupLabel)}>{g.label}</span>
            </div>
            {g.actions.map((action) => {
              const checked = scopes.has(action.id);
              const sel = scopes.get(action.id);
              const isOpen = expanded.has(action.id);
              return (
                <div key={action.id} className={css(pp.actionWrap)}>
                  <div className={css(pp.actionRow, readOnly && pp.actionRowDisabled)}>
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={checked}
                      onChange={() => toggleAction(action)}
                    />
                    <span className={css(pp.actionLabel)}>{action.label}</span>
                    {checked && sel && (
                      <button
                        type="button"
                        disabled={readOnly}
                        className={css(
                          pp.scopeChip,
                          action.dimension === 'org' && pp.scopeChipMuted,
                        )}
                        onClick={() => action.dimension !== 'org' && togglePicker(action.id)}
                        title={
                          action.dimension === 'org' ? 'Org-wide action' : 'Click to change scope'
                        }
                      >
                        {describeScope(action, sel, appNameById)}
                        {action.dimension !== 'org' && (
                          <span style={{ marginLeft: 6, opacity: 0.6 }}>{isOpen ? '▴' : '▾'}</span>
                        )}
                      </button>
                    )}
                    <code className={css(pp.actionId)}>{action.id}</code>
                  </div>
                  {checked && sel && isOpen && action.dimension !== 'org' && !readOnly && (
                    <ScopePicker
                      action={action}
                      selection={sel}
                      apps={resources.apps}
                      envsByAppId={resources.envsByAppId}
                      onChange={(next) => setActionScope(action.id, next)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {!readOnly && (
        <div
          className={css(s.modalActions)}
          style={{ justifyContent: 'space-between', marginTop: 16 }}
        >
          {onDelete ? (
            <button className={css(s.btnDanger)} onClick={onDelete}>
              Delete permission
            </button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={css(s.btnGhost)} onClick={onCancel}>
              Cancel
            </button>
            <button
              className={css(s.btn)}
              onClick={handleSave}
              disabled={busy || !name.trim() || scopes.size === 0}
            >
              {busy ? 'Saving…' : initial ? 'Save changes' : 'Create permission'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const pp = StyleSheet.create({
  split: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  list: {
    width: 280,
    minWidth: 280,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
    background: 'var(--td-white, #fff)',
    border: '1px solid var(--td-gray-100, #f3f3f3)',
    borderRadius: 8,
    padding: 8,
  },
  listItem: {
    background: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    padding: '10px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    color: 'var(--td-gray-900, #1a1a1a)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    position: 'relative' as const,
    ':hover': { background: 'var(--td-gray-50, #f9f9f9)' },
  },
  listItemActive: {
    background: 'var(--td-tile-blue-wash, #EEF4FA)',
    color: 'var(--td-tile-blue, #2068A8)',
  },
  listName: { fontSize: 13, fontWeight: 600 },
  listDesc: { fontSize: 11, color: 'var(--td-gray-500, #777)', lineHeight: 1.4 },
  builtinChip: {
    position: 'absolute' as const,
    top: 8,
    right: 8,
    fontSize: 9,
    color: 'var(--td-gray-500, #777)',
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  detail: {
    flex: 1,
    background: 'var(--td-white, #fff)',
    border: '1px solid var(--td-gray-100, #f3f3f3)',
    borderRadius: 8,
    padding: 24,
  },
  readOnlyBanner: {
    padding: 10,
    background: '#FFF8E0',
    border: '1px solid #F0D580',
    borderRadius: 4,
    color: '#7A5500',
    fontSize: 12,
    marginBottom: 12,
  },
  actionsSummary: {
    fontSize: 11,
    color: 'var(--td-gray-500, #777)',
    marginBottom: 8,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  actionGroups: { display: 'flex', flexDirection: 'column' as const, gap: 14 },
  actionGroup: {
    border: '1px solid var(--td-gray-100, #f3f3f3)',
    borderRadius: 6,
    padding: '8px 12px',
  },
  actionGroupHdr: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
    paddingBottom: 6,
    borderBottom: '1px solid var(--td-gray-50, #f9f9f9)',
  },
  actionGroupLabel: { fontSize: 12, fontWeight: 600, color: 'var(--td-gray-700, #484848)' },
  actionWrap: { padding: '2px 0' },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 0',
    fontSize: 12,
    color: 'var(--td-gray-900, #1a1a1a)',
    cursor: 'pointer',
  },
  actionRowDisabled: { opacity: 0.6, cursor: 'default' },
  actionLabel: { flex: 1 },
  actionId: {
    fontFamily: 'var(--mono, monospace)',
    fontSize: 10,
    color: 'var(--td-gray-500, #777)',
  },
  scopeChip: {
    background: 'var(--td-tile-blue-wash, #EEF4FA)',
    color: 'var(--td-tile-blue, #2068A8)',
    border: '1px solid transparent',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 11,
    cursor: 'pointer',
    ':hover': { borderColor: 'var(--td-tile-blue, #2068A8)' },
  },
  scopeChipMuted: {
    background: 'var(--td-gray-100, #f3f3f3)',
    color: 'var(--td-gray-500, #777)',
    cursor: 'default',
    ':hover': { borderColor: 'transparent' },
  },
});
